'use client';
import {useSweetAlert} from "@/hooks/useSweetAlert";
import {
  addItemsToLocalStorage,
  addToLocalStorage,
  deleteFromLocalStorage,
  getFromLocalStorage,
  getItemsFromLocalStorage
} from '@/libs/local-storage.util';
import { createContext, useContext, useEffect, useState } from "react";
import {useTranslations} from 'next-intl';
import {ClientTypes, ProductActionStatus} from '@/libs/constants/common.constants';
import {useUserContext} from '@/providers/UserContext';

const CartContext = createContext(null);

// cart: [{ product, quantity }]; cartTotalPrice: number
export default function CartContextProvider({ children, products, totalPrice }) {
  const t = useTranslations('cart')
  const {user} = useUserContext();
  const toastAlert = useSweetAlert();
  
  // const [cartStatus, setCartStatus] = useState(null);
  const [cartProducts, setCartProducts] = useState(Array.isArray(products) ? products : []);
  const [cartTotalPrice, setCartTotalPrice] = useState(totalPrice ?? 0);
  
  useEffect(() => {
    if (Array.isArray(products)) {
      addItemsToLocalStorage('cart', products);
    }
    
    const cartProductFromLocalStorage = getItemsFromLocalStorage('cart');
    if (cartProductFromLocalStorage) {
      setCartProducts(cartProductFromLocalStorage);
    }
  }, [products]);
  
  useEffect(() => {
    if (totalPrice != null) {
      addToLocalStorage('cartTotalPrice', totalPrice);
    }
    
    const cartTotalPriceFromLocalStorage = getFromLocalStorage('cartTotalPrice');
    if (cartTotalPriceFromLocalStorage != null) {
      setCartTotalPrice(cartTotalPriceFromLocalStorage === 'undefined' || cartTotalPriceFromLocalStorage === 'NaN' ? 0 : +cartTotalPriceFromLocalStorage);
    }
  }, [totalPrice]);
  
  
  const updateCartLocalStorage = (freshProducts, freshTotal) => {
    addItemsToLocalStorage('cart', freshProducts);
    addToLocalStorage('cartTotalPrice', freshTotal);
    setCartProducts(freshProducts);
    setCartTotalPrice(freshTotal);
  };
  
  const addProductToCart = (currentProduct, quantity, staticQuantity = false, toast = true) => {
    setCartProducts((prevCartProducts) => {
      const foundProductIndex = prevCartProducts.findIndex((cart) => cart.product.id === currentProduct.id);
      
      let updatedCart;
      let total = cartTotalPrice;
      
      if (foundProductIndex !== -1) {
        let tot = 0;
        updatedCart = prevCartProducts.map((cart, index) => {
          const parsedCart = index === foundProductIndex
              ? { ...cart, quantity: staticQuantity ? quantity : (cart.quantity + quantity) }
              : cart;
          
          tot += (user?.clientType === ClientTypes.WHOLESALER && parsedCart.product.price.wholesalePrice != null ? parsedCart.product.price.wholesalePrice : parsedCart.product.price.priceComputed) * parsedCart.quantity;
          return parsedCart;
        });
        total = tot;
        // setCartStatus(ProductActionStatus.increased);
        if (toast) {
          toastAlert('success', t('itemUpdatedInCart'));
        }
      } else {
        updatedCart = [...prevCartProducts, {
          product: {
            id: currentProduct.id,
            alt: currentProduct.alt,
            title: currentProduct.title,
            coverImage: currentProduct.coverImage,
            price: currentProduct.price,
          },
          quantity
        }];
        total += (user?.clientType === ClientTypes.WHOLESALER && currentProduct.price.wholesalePrice != null ? currentProduct.price.wholesalePrice : currentProduct.price.priceComputed) * quantity;
        // setCartStatus(ProductActionStatus.added);
        if (toast) {
          toastAlert('success', t('itemAddedToCart'));
        }
      }
      
      addItemsToLocalStorage('cart', updatedCart);
      addToLocalStorage('cartTotalPrice', total);
      setCartTotalPrice(total);
      return updatedCart;
    });
  };
  
  const deleteProductFromCart = (productId, toast = true) => {
    setCartProducts((prevCart) => {
      const foundIndex = prevCart.findIndex(({ product }) => product.id === productId);
      if (foundIndex === -1) return prevCart;
      
      const totalOfDeletedProduct = (user?.clientType === ClientTypes.WHOLESALER && prevCart[foundIndex].product.price.wholesalePrice != null ? prevCart[foundIndex].product.price.wholesalePrice : prevCart[foundIndex].product.price.priceComputed) * prevCart[foundIndex].quantity;
      const updatedCart = [...prevCart.slice(0, foundIndex), ...prevCart.slice(foundIndex + 1)];
      const remainedTotal = cartTotalPrice - totalOfDeletedProduct;
      
      addItemsToLocalStorage('cart', updatedCart);
      addToLocalStorage('cartTotalPrice', remainedTotal);
      if (toast) {
        toastAlert('success', t('deletedFromCart'));
      }
      // setCartStatus(ProductActionStatus.deleted);
      setCartTotalPrice(remainedTotal);
      return updatedCart;
    });
  };
  
  const clearProductsFromCart = () => {
    setCartProducts([]);
    setCartTotalPrice(0);
    deleteFromLocalStorage('cart');
    deleteFromLocalStorage('cartTotalPrice');
  }
  
  const getProductQuantityFromCart = (currentProduct) => {
    const pr = cartProducts.find((cart) => cart.product.id === currentProduct.id);
    return pr?.quantity ?? 0;
  };
  
  const getTotalQuantity = () => {
    return cartProducts.reduce((total, { quantity }) => total + quantity, 0);
  };
  
  return (
    <CartContext.Provider
      value={{
        cartProducts,
        cartTotalPrice,
        setCartProducts,
        addProductToCart,
        deleteProductFromCart,
        clearProductsFromCart,
        // cartStatus,
        getProductQuantityFromCart,
        getTotalQuantity,
        updateCartLocalStorage
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
export const useCartContext = () => {
  return useContext(CartContext);
};
